import type { ServiceInput } from '@seek/indie-api-types';
import { getReasonPhrase, StatusCodes } from 'http-status-codes';

const stringComparators = [
  'Equal',
  'NotEqual',
  'StartsWith',
  'EndsWith',
  'Contains',
];

const numericComparators = [
  'Equal',
  'NotEqual',
  'LessThan',
  'LessThanOrEqual',
  'GreaterThan',
  'GreaterThanOrEqual',
  'In',
  'NotIn',
];

const enumComparators = ['Equal', 'NotEqual', 'In', 'NotIn'];
const switchComparators = ['Equal', 'NotEqual'];

export type Comparator =
  | (typeof stringComparators)[number]
  | (typeof numericComparators)[number]
  | (typeof enumComparators)[number]
  | (typeof switchComparators)[number];

export const comparatorNames: Record<Comparator, string> = {
  Equal: 'is',
  NotEqual: 'is not',
  In: 'is one of',
  NotIn: 'is not one of',
  StartsWith: 'starts with',
  EndsWith: 'ends with',
  Contains: 'contains',
  LessThan: 'is less than',
  LessThanOrEqual: 'is less than or equal to',
  GreaterThan: 'is greater than',
  GreaterThanOrEqual: 'is greater than or equal to',
};

export const SpecialFilters = {
  graphql: '$forced_graphql',
  hirer: '$forced_hirer',
  notOk: '$not_ok',
};

export const specialConfigs = {
  [SpecialFilters.graphql]: {
    type: 'RequestType',
    value: 'GraphQL',
    label: 'GraphQL requests',
    explanation: (groupByLabel: string) =>
      `${groupByLabel} only applies to GraphQL requests. Change your ‘group by’ to remove this filter.`,
  },
  [SpecialFilters.hirer]: {
    type: 'UsageType',
    value: 'browser',
    label: 'Browser tokens',
    explanation: () => 'Hirer information is only available for browser tokens',
  },
  [SpecialFilters.notOk]: {
    type: 'Status',
    value: 'OK',
    label: 'OK',
    comparator: 'NotEqual',
    explanation: (groupByLabel: string) =>
      `${groupByLabel} only applies to requests with errors. Change your ‘group by’ to remove this filter.`,
  },
} as const;

export const StatusNames = {
  OK: 'OK',
  ClientError: 'Client error',
  ServerError: 'Server error',
};

export const UsageTypeNames = {
  partner: 'Partner tokens',
  browser: 'Browser tokens',
};

export const OperationTypeNames = {
  query: 'Query',
  mutation: 'Mutation',
};

export const filterTypes = [
  {
    type: 'RequestType',
    label: 'Request type',
    comparators: switchComparators,
    values: [
      { value: 'GraphQL', label: 'GraphQL requests' },
      { value: 'Attachment', label: 'Attachment requests' },
    ],
  },
  {
    type: 'Status',
    label: 'Status',
    comparators: enumComparators,
    values: Object.entries(StatusNames).map(([value, label]) => ({
      value,
      label,
    })),
  },
  {
    type: 'UsageType',
    label: 'Token type',
    comparators: enumComparators,
    values: Object.entries(UsageTypeNames).map(([value, label]) => ({
      value,
      label,
    })),
  },
  {
    type: 'GraphQLOperationType',
    label: 'Operation type',
    comparators: enumComparators,
    values: Object.entries(OperationTypeNames).map(([value, label]) => ({
      value,
      label,
    })),
    requireFilter: [SpecialFilters.graphql],
  },
  {
    type: 'StatusCode',
    label: 'HTTP Status Code',
    comparators: numericComparators,
    values: Object.values(StatusCodes)
      .filter((v) => typeof v === 'number')
      .map((code) => ({
        label: `${code}: ${getReasonPhrase(code)}`,
        value: code.toString(),
      })),
  },
  {
    type: 'IpAddress',
    label: 'IP Address',
    comparators: stringComparators,
  },
  {
    type: 'UserAgent',
    label: 'User agent',
    comparators: stringComparators,
  },
  {
    type: 'UserProvidedGraphQLOperationName',
    label: 'Operation name',
    comparators: stringComparators,
    requireFilter: [SpecialFilters.graphql],
  },
  {
    type: 'GraphQLOperationFieldName',
    label: 'Query/mutation name',
    comparators: stringComparators,
    requireFilter: [SpecialFilters.graphql],
  },
  {
    type: 'GraphQLField',
    label: 'GraphQL field',
    comparators: stringComparators,
    requireFilter: [SpecialFilters.graphql],
  },
  {
    type: 'HirerOID',
    label: 'Hirer',
    comparators: switchComparators,
    placeholder: 'Hirer ID (e.g. seekAnz:organisation:seek:abcdef)',
    requireFilter: [SpecialFilters.hirer],
  },
  {
    type: 'PartnerProvidedRequestId',
    label: 'Request ID',
    comparators: stringComparators,
  },
  {
    type: 'GraphQLOperationErrorCode',
    label: 'GraphQL error code',
    comparators: stringComparators,
    requireFilter: [SpecialFilters.graphql, SpecialFilters.notOk],
  },
  {
    type: 'GraphQLOperationErrorMessage',
    label: 'GraphQL error message',
    comparators: stringComparators,
    requireFilter: [SpecialFilters.graphql, SpecialFilters.notOk],
  },
]
  .map((config) => {
    const valueMap = config.values
      ? new Map(config.values.map(({ value, label }) => [value, label]))
      : undefined;
    const labelMap = new Map(
      [...(valueMap?.entries() ?? [])].map(([k, v]) => [v, k]),
    );

    return {
      ...config,
      valueMap,
      labelMap,
    };
  })
  .toSorted((a, b) => a.label.localeCompare(b.label));

export const filterTypesMap = new Map(
  filterTypes.map((config) => [config.type, config]),
);

export type FilterType = (typeof filterTypes)[number]['type'];

export const groupByOptions = [
  // Sort by presumed usefulness
  'Status',
  'GraphQLOperationFieldName',
  'UserProvidedGraphQLOperationName',
  'RequestType',
  'GraphQLOperationErrorCode',
  'GraphQLOperationType',
  'HirerOID',
  'GraphQLOperationErrorMessage',
  'UsageType',
  'StatusCode',
  'IpAddress',
  'UserAgent',
] as const;

export type GroupBy = (typeof groupByOptions)[number] | 'None';

export const mapSeriesLabelToFilterLabel = (
  seriesLabel: string,
  groupBy: ServiceInput.IntegrationStatistics['groupBy'],
): string =>
  filterTypesMap.get(groupBy as never)?.valueMap?.get(seriesLabel) ??
  seriesLabel;

export const mapFilterLabelToFilterValue = (
  label: string,
  groupBy: ServiceInput.IntegrationStatistics['groupBy'],
): string | number | Array<string | number> =>
  filterTypesMap.get(groupBy as never)?.labelMap.get(label.toString()) ??
  label.toString();
